import {
	PushNotificationService,
	Settings,
	SafariSettings
} from "@scopely/web-sdk-push-notifications";
import { analyticsTrackerV2 } from "contexts/ScopelyIdentityProvider/configv2";
import { getServiceWorkerPath } from "helpers/serviceWorker";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_PUSH_VAPID_PUBLIC_KEY || "";
const SERVICE_WORKER_PATH = getServiceWorkerPath();
const SAFARI_WEB_PACKAGE_ID =
	process.env.REACT_APP_PUSH_SAFARI_WEB_PACKAGE_ID || "";
const SAFARI_WEB_SERVICE_URL =
	process.env.REACT_APP_PUSH_SAFARI_WEB_SERVICE_URL ||
	"https://pigeon.aprod.scopely.io/spn";

let pushService: PushNotificationService | null = null;

const getOrCreatePushService = (): PushNotificationService | null => {
	if (typeof window === "undefined") {
		return null;
	}

	if (!VAPID_PUBLIC_KEY) {
		// eslint-disable-next-line no-console
		// console.warn(
		// 	"[Playgami Push] Missing REACT_APP_PUSH_VAPID_PUBLIC_KEY, push will be disabled"
		// );
		return null;
	}

	if (!pushService) {
		// eslint-disable-next-line no-console
		// console.log("[Playgami Push] Creating PushNotificationService", {
		// 	workerPath: SERVICE_WORKER_PATH,
		// 	vapidConfigured: Boolean(VAPID_PUBLIC_KEY),
		// 	safariConfigured: Boolean(SAFARI_WEB_PACKAGE_ID && SAFARI_WEB_SERVICE_URL)
		// });
		const settings = new Settings(SERVICE_WORKER_PATH, VAPID_PUBLIC_KEY);
		// Optional Safari configuration: only enabled when both env vars exist.
		if (SAFARI_WEB_PACKAGE_ID && SAFARI_WEB_SERVICE_URL) {
			const safariSettings = new SafariSettings(
				SAFARI_WEB_PACKAGE_ID,
				SAFARI_WEB_SERVICE_URL
			);
			settings.setSafariSettings(safariSettings);
		}
		// Type-level note:
		// There can be multiple copies of @scopely/web-sdk-analytics in node_modules
		// (e.g. one nested under @scopely/standard-events), which makes the
		// BrowserTracker type appear incompatible with the Tracker type used
		// by web-sdk-push-notifications. At runtime they are equivalent, so we
		// define a local compatible signature that accepts any tracker shape.
		const PushNotificationServiceCompat =
			PushNotificationService as unknown as {
				browser: (
					settings: Settings,
					tracker: unknown
				) => PushNotificationService | null;
			};
		pushService = PushNotificationServiceCompat.browser(
			settings,
			analyticsTrackerV2
		);
	}

	return pushService;
};

export const isPlaygamiPushAvailable = (): boolean => {
	const service = getOrCreatePushService();
	if (!service) {
		return false;
	}
	return service.isPushAvailable();
};

export const getPlaygamiSystemPermission = ():
	| NotificationPermission
	| "unsupported" => {
	const service = getOrCreatePushService();
	if (!service || typeof Notification === "undefined") {
		return "unsupported";
	}
	return service.getSystemPermission() as NotificationPermission;
};

export const getPlaygamiNotificationsEnabled = (): boolean => {
	const service = getOrCreatePushService();
	if (!service) {
		return false;
	}
	return Boolean(service.getNotificationEnabled());
};

export const subscribePlaygamiPush = async (userId: string): Promise<void> => {
	const service = getOrCreatePushService();
	if (!service) {
		return;
	}
	await service.subscribeToPushNotifications(userId);
};

export const unsubscribePlaygamiPush = async (): Promise<void> => {
	const service = getOrCreatePushService();
	if (!service) {
		return;
	}
	await service.unsubscribeToPushNotifications();
};

export const startPlaygamiPush = async (userId: string): Promise<void> => {
	const service = getOrCreatePushService();
	if (!service) {
		return;
	}
	await service.start(userId);
};
